if (!customElements.get('td-multi-product-hero')) {
  class TDMultiProductHero extends HTMLElement {
    static HIGHLIGHT_RETURN_DELAY = 160;

    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('[data-td-product-card]'));
      this.panels = Array.from(this.querySelectorAll('[data-td-product-panel]'));
      this.copies = Array.from(this.querySelectorAll('[data-td-product-copy]'));
      this.headings = Array.from(this.querySelectorAll('[data-td-product-heading]'));
      this.rating = this.querySelector('[data-td-product-rating]');
      this.stage = this.querySelector('[data-td-card-stage]');
      this.desktopQuery = window.matchMedia('(min-width: 1180px)');
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.activeBlockId = (this.cards.find((card) => card.dataset.tdSelected === 'true') || this.cards[0])?.dataset.tdBlockId;
      this.hoveredBlockId = null;
      this.highlightedBlockId = this.activeBlockId;
      this.suppressScrollSelectionUntil = 0;
      this.scrollFrame = null;

      if (!this.cards.length || !this.panels.length || !this.stage) {
        this.setAttribute('data-td-ready', 'true');
        return;
      }

      this.listeners = new AbortController();
      const { signal } = this.listeners;

      this.cards.forEach((card) => {
        card.addEventListener('click', () => this.handleCardClick(card), { signal });
        card.addEventListener('mouseenter', () => this.handleCardHover(card), { signal });
        card.addEventListener('mouseleave', () => this.handleCardHoverEnd(), { signal });
      });
      document.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), { signal });
      this.stage.addEventListener('scroll', () => this.handleStageScroll(), { passive: true, signal });
      // A real touch/drag takes over from any pending programmatic scroll
      this.stage.addEventListener('pointerdown', () => { this.suppressScrollSelectionUntil = 0; }, { signal });
      this.desktopQuery.addEventListener('change', () => this.handleViewportChange(), { signal });

      this.applyState();
      this.positionMobileCarousel('auto');
      this.initReveal();
      this.setAttribute('data-td-ready', 'true');
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.revealObserver?.disconnect();
      window.clearTimeout(this.revealTimeout);
      window.clearTimeout(this.motionTimeout);
      window.clearTimeout(this.highlightReturnTimeout);
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
    }

    isDesktop() {
      return this.desktopQuery.matches;
    }

    activeCard() {
      return this.cards.find((card) => card.dataset.tdBlockId === this.activeBlockId) || null;
    }

    handleCardClick(card) {
      this.selectBlock(card.dataset.tdBlockId, {
        focusCard: this.isDesktop(),
        scrollIntoView: !this.isDesktop(),
      });
    }

    nudgeCard(card) {
      if (!this.isDesktop()) return;
      card.removeAttribute('data-td-nudge');
      void card.offsetWidth; // reflow so the animation can replay
      card.setAttribute('data-td-nudge', '');
      card.addEventListener('animationend', () => card.removeAttribute('data-td-nudge'), { once: true });
    }

    setHighlight(blockId) {
      if (blockId === this.highlightedBlockId) return;
      const previousCard = this.cards.find((card) => card.dataset.tdBlockId === this.highlightedBlockId);
      this.highlightedBlockId = blockId;
      if (!this.isDesktop()) return;
      const card = this.cards.find((c) => c.dataset.tdBlockId === blockId);
      if (card) {
        const previousOrder = Number(previousCard?.dataset.tdDesktopOrder);
        const nextOrder = Number(card.dataset.tdDesktopOrder);
        const nudgeDirection = nextOrder > previousOrder ? 1 : -1;
        card.style.setProperty('--td-card-nudge-direction', String(nudgeDirection));
        this.nudgeCard(card);
      }
    }

    handleCardHover(card) {
      if (!this.isDesktop()) return;
      window.clearTimeout(this.highlightReturnTimeout);
      this.hoveredBlockId = card.dataset.tdBlockId;
      this.showPanel(this.hoveredBlockId);
      this.applyCardFilters(this.hoveredBlockId);
      this.setHighlight(this.hoveredBlockId);
    }

    handleCardHoverEnd() {
      if (!this.isDesktop()) return;
      window.clearTimeout(this.highlightReturnTimeout);
      this.highlightReturnTimeout = window.setTimeout(() => {
        this.hoveredBlockId = null;
        this.applyState();
      }, TDMultiProductHero.HIGHLIGHT_RETURN_DELAY);
    }

    handleBlockSelect(event) {
      if (!(event.target instanceof HTMLElement) || !this.contains(event.target)) return;
      const card = event.target.closest('[data-td-product-card]');
      if (!(card instanceof HTMLElement)) return;
      this.selectBlock(card.dataset.tdBlockId, { scrollIntoView: !this.isDesktop() });
    }

    handleViewportChange() {
      this.applyState();
      this.positionMobileCarousel('auto');
      this.initReveal();
    }

    // Mobile: the card nearest the stage center becomes active while scrolling
    handleStageScroll() {
      if (this.isDesktop() || this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        if (performance.now() < this.suppressScrollSelectionUntil) return;

        const card = this.getCardNearestCenter();
        if (card && card.dataset.tdBlockId !== this.activeBlockId) {
          this.activeBlockId = card.dataset.tdBlockId;
          this.applyState();
        }
      });
    }

    getCardNearestCenter() {
      const maxScroll = this.stage.scrollWidth - this.stage.clientWidth;
      // Edge cards win at the extremes so they are always reachable
      if (this.stage.scrollLeft <= 2) return this.cards[0];
      if (maxScroll > 0 && this.stage.scrollLeft >= maxScroll - 2) return this.cards[this.cards.length - 1];

      const stageBounds = this.stage.getBoundingClientRect();
      const stageCenter = stageBounds.left + stageBounds.width / 2;
      let nearest = null;
      let nearestDistance = Infinity;

      this.cards.forEach((card) => {
        const bounds = card.getBoundingClientRect();
        if (bounds.right <= stageBounds.left || bounds.left >= stageBounds.right) return;
        const distance = Math.abs(bounds.left + bounds.width / 2 - stageCenter);
        if (distance < nearestDistance) {
          nearest = card;
          nearestDistance = distance;
        }
      });

      return nearest;
    }

    // Mobile: center track, or shift so active card is fully visible.
    positionMobileCarousel(behavior) {
      if (this.isDesktop()) return;
      const maxScroll = this.stage.scrollWidth - this.stage.clientWidth;
      if (maxScroll <= 0) return;
      this.scrollStageTo(this.revealScrollTarget(this.activeCard(), maxScroll / 2), behavior);
    }

    // Smallest shift from `base` that makes `card` fully visible
    revealScrollTarget(card, base) {
      const maxScroll = Math.max(0, this.stage.scrollWidth - this.stage.clientWidth);
      const target = Math.min(maxScroll, Math.max(0, base));
      if (!card) return target;

      const left = card.offsetLeft;
      const right = left + card.offsetWidth;
      if (left >= target && right <= target + this.stage.clientWidth) return target;

      if (card === this.cards[0]) return 0;
      if (card === this.cards[this.cards.length - 1]) return maxScroll;
      if (left < target) return Math.max(0, left);
      return Math.min(maxScroll, right - this.stage.clientWidth);
    }

    scrollStageTo(left, behavior) {
      if (Math.abs(left - this.stage.scrollLeft) < 1) return;
      this.suppressScrollSelectionUntil = performance.now() + (behavior === 'smooth' ? 700 : 150);
      if (behavior === 'smooth' && !this.prefersReducedMotion) {
        this.stage.scrollTo({ left, behavior: 'smooth' });
      } else {
        this.stage.scrollLeft = left;
      }
    }

    // Entry animation: cards lift in (staggered) once the stage scrolls into view
    initReveal() {
      this.revealObserver?.disconnect();
      this.revealObserver = null;

      if (this.getAttribute('data-td-motion') === 'complete') return;
      if (this.prefersReducedMotion) {
        this.setAttribute('data-td-motion', 'complete');
        return;
      }

      this.setAttribute('data-td-motion', 'pending');
      this.revealObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.reveal();
      }, { threshold: 0.25 });
      this.revealObserver.observe(this.stage);
    }

    reveal() {
      this.revealObserver?.disconnect();
      this.revealObserver = null;

      let started = false;
      const start = () => {
        if (started) return;
        started = true;
        window.clearTimeout(this.revealTimeout);
        this.setAttribute('data-td-motion', 'ready');
        this.motionTimeout = window.setTimeout(() => this.setAttribute('data-td-motion', 'complete'), 900);
      };

      // Wait for card images to decode, but never longer than 600ms
      this.revealTimeout = window.setTimeout(start, 600);
      const images = this.cards.map((card) => card.querySelector('img')).filter(Boolean);
      Promise.all(
        images.map((img) => {
          if (img.complete && img.naturalWidth) return img.decode ? img.decode().catch(() => {}) : Promise.resolve();
          return new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          });
        })
      ).then(() => window.requestAnimationFrame(start));
    }

    showPanel(blockId) {
      this.panels.forEach((panel) => {
        panel.setAttribute('aria-hidden', panel.dataset.tdBlockId === blockId ? 'false' : 'true');
      });
      this.copies.forEach((copy) => {
        copy.setAttribute('aria-hidden', copy.dataset.tdBlockId === blockId ? 'false' : 'true');
      });
      this.headings.forEach((heading) => {
        heading.setAttribute('aria-hidden', heading.dataset.tdBlockId === blockId ? 'false' : 'true');
      });
      this.updateRating(blockId);
    }

    updateRating(blockId) {
      if (!this.rating) return;

      const card = this.cards.find((item) => item.dataset.tdBlockId === blockId);
      const productId = card?.dataset.tdProductId;
      if (!productId) {
        this.rating.replaceChildren();
        return;
      }

      let widget = Array.from(this.rating.querySelectorAll('[data-td-product-rating-widget]'))
        .find((item) => item.dataset.id === productId);

      if (!widget) {
        widget = document.createElement('div');
        widget.className = 'klaviyo-star-rating-widget';
        widget.dataset.tdProductRatingWidget = '';
        widget.dataset.id = productId;
        widget.dataset.productTitle = card.dataset.tdProductTitle || '';
        widget.dataset.productType = card.dataset.tdProductType || '';
        this.rating.append(widget);
      }

      this.rating.querySelectorAll('[data-td-product-rating-widget]').forEach((item) => {
        const isActive = item === widget;
        item.hidden = !isActive;
        item.setAttribute('aria-hidden', String(!isActive));
      });
    }

    applyCardFilters(previewBlockId = null) {
      const isPreviewing = previewBlockId != null && previewBlockId !== this.activeBlockId;
      const topZ = this.cards.length + 1;
      this.cards.forEach((card) => {
        const blockId = card.dataset.tdBlockId;
        const fullColor = previewBlockId ? blockId === previewBlockId : blockId === this.activeBlockId;
        const isSelected = blockId === this.activeBlockId;
        const desktopOrder = Number(card.dataset.tdDesktopOrder) || 1;
        const isPreviewCard = isPreviewing && blockId === previewBlockId;
        card.style.setProperty(
          '--td-card-filter',
          fullColor ? 'var(--td-card-active-filter)' : 'var(--td-card-inactive-filter)'
        );
        const selectedZIndex = isSelected && !isPreviewing ? topZ : desktopOrder;
        card.style.setProperty('--td-card-z-index', String(selectedZIndex));
        card.style.setProperty('--td-card-selected-z-index', String(selectedZIndex));
        if (isPreviewCard) {
          card.style.setProperty('--td-card-hover-z-index', String(topZ + 1));
        } else {
          card.style.removeProperty('--td-card-hover-z-index');
        }
      });
    }

    applyState() {
      if (!this.activeBlockId) return;

      const topZ = this.cards.length + 1;
      this.cards.forEach((card) => {
        const isSelected = card.dataset.tdBlockId === this.activeBlockId;
        card.dataset.tdSelected = String(isSelected);
        card.setAttribute('aria-pressed', String(isSelected));
        card.style.setProperty('--td-card-z-index', String(isSelected ? topZ : Number(card.dataset.tdDesktopOrder) || 1));
      });

      this.setHighlight(this.hoveredBlockId || this.activeBlockId);
      this.applyCardFilters();
      this.showPanel(this.activeBlockId);
    }

    selectBlock(blockId, { focusCard = false, scrollIntoView = false } = {}) {
      const card = this.cards.find((c) => c.dataset.tdBlockId === blockId);
      if (!card) return;

      this.activeBlockId = blockId;
      this.applyState();

      if (scrollIntoView) {
        this.scrollStageTo(this.revealScrollTarget(card, this.stage.scrollLeft), 'smooth');
      }
      if (focusCard) {
        card.focus({ preventScroll: true });
      }
    }
  }

  customElements.define('td-multi-product-hero', TDMultiProductHero);
}
